import { without } from 'es-toolkit';
import React from 'react';
import { FormControl, InputLabel, ListItemText, MenuItem, Select, Typography } from '@mui/material';

import { LumberGrade, LumberSpecies, LumberTreatmentType } from '@paragon/contract-library';
import { humanCase } from '@paragon/utilities';

type SelectableLumberField = 'species' | 'grade' | 'treatmentType';

type Value<TType extends SelectableLumberField> = TType extends 'species'
  ? LumberSpecies
  : TType extends 'grade'
    ? LumberGrade
    : TType extends 'treatmentType'
      ? LumberTreatmentType
      : never;

interface Props<TType extends SelectableLumberField> {
  type: TType;

  values: readonly Value<TType>[];
  selectedValues: readonly Value<TType>[];

  errorMessage?: string | null;

  disabled?: boolean;
  fullWidth?: boolean;

  onSelectedValuesChange(newValues: Value<TType>[]): void;

  formatSingleValue: (value: Value<TType>) => string;
  formatMultipleValues: (values: Value<TType>[]) => string;
}

export function LumberPropertySelectDialog<TType extends SelectableLumberField>(props: Props<TType>) {
  return (
    <FormControl size="small" sx={{ m: 1, flex: 1 }} fullWidth={props.fullWidth}>
      <InputLabel id={`${props.type}-label`}>{humanCase(props.type)}</InputLabel>
      <Select
        multiple
        labelId={`${props.type}-label`}
        value={props.selectedValues}
        renderValue={props.formatMultipleValues as (values: unknown) => string}
        label={humanCase(props.type)}
        disabled={props.disabled}
      >
        {props.errorMessage ? <Typography color="error">{props.errorMessage}</Typography> : null}
        {props.values.map((value) => (
          <MenuItem
            key={value}
            value={value}
            onClick={() => {
              if (props.selectedValues.includes(value)) {
                props.onSelectedValuesChange(without(props.selectedValues, value));
              } else {
                props.onSelectedValuesChange(props.selectedValues.concat(value));
              }
            }}
          >
            <ListItemText primary={props.formatSingleValue(value)} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
